import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { ButtonSize } from '~/ui-rocco/button-system';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import {
    trackArticleContactCtaClick,
    trackArticleViewProfileClick,
    type ArticleCtaPlacement,
} from '~/modules/tracking/util/trackArticleProfessionalCta';

interface RevampProfessionalCtasProps {
    professional: { title: string; link: string };
    /** Which module hosts the pair — carried in every click's ExtraData. */
    placement: ArticleCtaPlacement;
    className?: string;
    /** Small pills for compact placements (e.g. under an image figure). */
    compact?: boolean;
}

/**
 * "View Profile" / "Contact" pill pair used by the revamp quote and image
 * modules (Figma 2479:70492). Derived from the article's professional — not
 * page-builder content. Clicks are tracked per placement (BC-4876): View
 * Profile keeps the legacy `ProductProfileButtonClick` series, Contact logs
 * the new `ArticleContactCtaClick`.
 */
export const RevampProfessionalCtas = ({
    professional,
    placement,
    className,
    compact = false,
}: RevampProfessionalCtasProps) => {
    const tracker = useTracker();

    if (!professional.link) return null;

    // The detail pages' own CTA, at the step the placement asks for: L is what
    // product detail sets its buttons at, S for the pair tucked under an image.
    // Nothing here names a height, a corner or a colour — those belong to the
    // size and the kind.
    const size: ButtonSize = compact ? 's' : 'l';

    return (
        <div className={cn('flex items-center gap-[0.625rem]', className)}>
            <RoccoLink
                to={professional.link}
                onClick={() => trackArticleViewProfileClick(tracker, placement)}
                className={detailCta('filled-light', undefined, size)}
            >
                View Profile
            </RoccoLink>
            <RoccoLink
                to={`${professional.link}/contact`}
                onClick={() => trackArticleContactCtaClick(tracker, placement)}
                className={detailCta('outlined-light', undefined, size)}
            >
                Contact
            </RoccoLink>
        </div>
    );
};
